import {SEARCH_DELAY_IN_MS} from '@hconnect/common/consts'
import {ControlledAutocompleteSelect} from '@hm/fundamental'
import {useDebounce} from '@react-hook/debounce'
import {memo, useCallback, useEffect, useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {useRefractoriesOptions} from '@refra/modules/common/hooks/queries'
import type {Refractory} from '@refra/modules/refra-catalog/types'

interface ProductSelectProps {
  manufacturerId?: number
  plantUpmId?: string
  selectedValue?: number | null
  error?: string
  isDisabled?: boolean
  isTouched?: boolean
  onChange: (refractoryId: number | null | undefined) => void
  onRefractoryChange?: (refractory: Refractory | null) => void
  autoFocus?: boolean
  initialSearchTerm?: string
  fallbackRefractory?: Refractory
  dataCy?: string
}

export const ProductSelect = memo(
  ({
    manufacturerId,
    plantUpmId,
    selectedValue,
    error,
    isDisabled,
    isTouched,
    onChange,
    onRefractoryChange,
    autoFocus = false,
    initialSearchTerm = '',
    fallbackRefractory,
    dataCy = 'add_material_product_select'
  }: ProductSelectProps) => {
    const {t} = useTranslation()
    const productSelectRef = useRef<HTMLDivElement>(null)
    const sanitizeSearchTerm = (value: string) =>
      value
        .trim()
        .slice(0, 100)
        .replace(/[<>{}]/g, '')
    const [searchTerm, setSearchTerm] = useDebounce(
      sanitizeSearchTerm(initialSearchTerm),
      SEARCH_DELAY_IN_MS
    )
    const hasAutoFocused = useRef(false)
    const wasFetching = useRef(false)

    const requiresManufacturer = !fallbackRefractory
    const isQueryEnabled = requiresManufacturer
      ? manufacturerId !== undefined
      : plantUpmId !== undefined

    const {refractoriesOptions, areRefractoriesFetching, refractoriesMap} = useRefractoriesOptions({
      currentManufacturerId: manufacturerId,
      refractoriesSearch: searchTerm,
      plantUpmId,
      enabled: isQueryEnabled
    })

    const enhancedOptions = useMemo(() => {
      if (!fallbackRefractory) return refractoriesOptions

      const hasFallback = refractoriesOptions.some((opt) => opt.value === fallbackRefractory.id)
      if (hasFallback) return refractoriesOptions

      return [
        {value: fallbackRefractory.id, label: fallbackRefractory.name.slice(0, 200)},
        ...refractoriesOptions
      ]
    }, [refractoriesOptions, fallbackRefractory])

    const enhancedRefractoriesMap = useMemo(() => {
      if (!fallbackRefractory) return refractoriesMap

      if (refractoriesMap[fallbackRefractory.id]) return refractoriesMap

      return {...refractoriesMap, [fallbackRefractory.id]: fallbackRefractory}
    }, [refractoriesMap, fallbackRefractory])

    const isFieldDisabled = isDisabled || !isQueryEnabled

    const handleFilterChange = useCallback(
      (value: string) => {
        setSearchTerm(sanitizeSearchTerm(value))
      },
      [setSearchTerm]
    )

    const handleChange = useCallback(
      (selectedIds: (string | number)[]) => {
        const refractoryId = selectedIds[0] as number | undefined
        onChange(refractoryId ?? null)

        if (onRefractoryChange) {
          const refractory = refractoryId ? (enhancedRefractoriesMap[refractoryId] ?? null) : null
          onRefractoryChange(refractory)
        }
      },
      [onChange, onRefractoryChange, enhancedRefractoriesMap]
    )

    useEffect(() => {
      if (onRefractoryChange && selectedValue) {
        const refractory = enhancedRefractoriesMap[selectedValue] ?? null
        onRefractoryChange(refractory)
      }
    }, [enhancedRefractoriesMap, selectedValue, onRefractoryChange])

    useEffect(() => {
      const shouldFocusOnInitial =
        autoFocus && !hasAutoFocused.current && !isFieldDisabled && isQueryEnabled
      const shouldFocusAfterFetch =
        autoFocus && wasFetching.current && !areRefractoriesFetching && productSelectRef.current

      if (shouldFocusOnInitial || shouldFocusAfterFetch) {
        const input = productSelectRef.current?.querySelector('input')
        input?.focus()
        if (shouldFocusOnInitial) {
          hasAutoFocused.current = true
        }
      }

      wasFetching.current = areRefractoriesFetching
    }, [autoFocus, isFieldDisabled, isQueryEnabled, areRefractoriesFetching])

    useEffect(() => {
      hasAutoFocused.current = false
      wasFetching.current = false
    }, [manufacturerId])

    return (
      <div ref={productSelectRef} className="flex-1">
        <ControlledAutocompleteSelect
          label={t('refraPlanning.materialInstallations.product')}
          options={enhancedOptions}
          selectedValues={[selectedValue ?? '']}
          error={error}
          isDisabled={isFieldDisabled}
          isTouched={isTouched}
          onFilterChange={handleFilterChange}
          onChange={handleChange}
          data-cy={dataCy}
        />
      </div>
    )
  }
)
